// Version: 1.4
// v1.4: Inputs and selects take their look from index.css (input-sm for compact) (CV-50).
// v1.3: Buttons use the shared button system (CV-49): 8px corners, standard variants and sizes, one selected look; no inline radius, padding or font size.
// v1.2: Add hub field to WIDGET_CATALOGUE; fetch client features; show locked
//       state for widgets whose hub is disabled. Admin can still toggle enabled
//       state on locked widgets (pre-configuration) but they won't render on the
//       dashboard until the hub is activated in Subscription settings.

import React, { useEffect, useState, useCallback } from 'react';
import { authFetch } from './auth';

const HUB_LABELS = {
  hub_cyber:        'Cyber Security Hub',
  hub_device:       'Device Hub',
  hub_compliance:   'Compliance Hub',
  hub_digital:      'Digital Hub',
  hub_productivity: 'Productivity Hub',
  hub_assets:       'Assets Hub',
};

const WIDGET_CATALOGUE = [
  { id: 'cyber_grade',    label: 'Cyber Security Grade',  size: '1x1', hub: 'hub_cyber',        desc: 'Overall grade and score from the latest assessment.' },
  { id: 'compliance',     label: 'Compliance',            size: '1x1', hub: 'hub_compliance',   desc: 'Framework progress rings — CE+, GDPR, ISO 27001.' },
  { id: 'productivity',   label: 'Microsoft 365',         size: '1x1', hub: 'hub_productivity', desc: 'Secure Score and MFA adoption percentage.' },
  { id: 'devices',        label: 'Device Security',       size: '1x1', hub: 'hub_device',       desc: 'Managed computer count and compliance status.' },
  { id: 'backup',         label: 'Backup Status',         size: '1x1', hub: 'hub_compliance',   desc: 'Backup task health from edge node data.' },
  { id: 'tickets',        label: 'Support Tickets',       size: '1x1', hub: null,               desc: 'Open, critical and total ticket count.' },
  { id: 'email_security', label: 'Email Security',        size: '1x1', hub: 'hub_cyber',        desc: 'SPF, DKIM and DMARC status from last assessment.' },
  { id: 'alerts',         label: 'UK Cyber Alerts',       size: '1x1', hub: null,               desc: 'Latest NCSC threat advisories.' },
  { id: 'web_presence',   label: 'Website, SEO and Geo',  size: '2x1', hub: 'hub_digital',      desc: 'PageSpeed, SSL and domain tracking. Wide tile.' },
  { id: 'activity',       label: 'Recent Activity',       size: '2x1', hub: null,               desc: 'Assessment history and platform events. Wide tile.' },
  { id: 'saas',           label: 'SaaS Discovery',        size: '1x1', hub: 'hub_cyber',        desc: 'Detected cloud services from DNS scan.' },
  { id: 'phishing',       label: 'Phishing Simulation',   size: '1x1', hub: null,               desc: 'Campaign click rates and awareness trend.' },
];

const DEFAULT_NAV_LABELS = {
  dashboard:       'Dashboard',
  tickets:         'Tickets',
  cyber_security:  'Cyber Security Hub',
  domains:         'Digital Hub',
  devices:         'Device Hub',
  assets:          'Assets Hub',
  compliance:      'Compliance Hub',
  productivity:    'Productivity Hub',
  schedules:       'Schedules',
  resources:       'Resources',
  reports:         'Reports',
};

const DEFAULT_CONFIG = {
  widgets: WIDGET_CATALOGUE
    .filter(w => ['cyber_grade','compliance','productivity','devices','backup','tickets','email_security','alerts','web_presence','activity'].includes(w.id))
    .map(w => ({ id: w.id, enabled: true, size: w.size })),
  nav_labels: { ...DEFAULT_NAV_LABELS },
};

export default function DashboardAdmin({ slug }) {
  const [config,   setConfig]   = useState(null);
  const [features, setFeatures] = useState({});
  const [saving,   setSaving]   = useState(false);
  const [saved,    setSaved]    = useState(false);
  const [tab,      setTab]      = useState('widgets');
  const [dragging, setDragging] = useState(null);
  const [dragOver, setDragOver] = useState(null);

  // Load config
  useEffect(() => {
    if (!slug) return;
    authFetch(`/api/clients/${slug}/dashboard-config`)
      .then(r => r.json())
      .then(d => {
        if (d?.widgets) {
          const existing = new Set(d.widgets.map(w => w.id));
          const merged   = [...d.widgets];
          WIDGET_CATALOGUE.forEach(w => {
            if (!existing.has(w.id)) merged.push({ id: w.id, enabled: false, size: w.size });
          });
          setConfig({ ...DEFAULT_CONFIG, ...d, widgets: merged, nav_labels: { ...DEFAULT_NAV_LABELS, ...(d.nav_labels || {}) } });
        } else {
          const all = WIDGET_CATALOGUE.map(w => ({ id: w.id, enabled: DEFAULT_CONFIG.widgets.some(d => d.id === w.id), size: w.size }));
          setConfig({ ...DEFAULT_CONFIG, widgets: all });
        }
      })
      .catch(() => {
        const all = WIDGET_CATALOGUE.map(w => ({ id: w.id, enabled: DEFAULT_CONFIG.widgets.some(d => d.id === w.id), size: w.size }));
        setConfig({ ...DEFAULT_CONFIG, widgets: all });
      });
  }, [slug]);

  // Load features — used to show locked state on hub-disabled widgets
  useEffect(() => {
    if (!slug) return;
    authFetch(`/api/clients/${slug}/features`)
      .then(r => r.json())
      .then(d => setFeatures(d?.features || {}))
      .catch(() => {});
  }, [slug]);

  const hubActive = (hubKey) => !hubKey || features[hubKey];

  const save = async () => {
    setSaving(true);
    try {
      await authFetch(`/api/clients/${slug}/dashboard-config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...config, last_updated: new Date().toISOString() }),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {}
    setSaving(false);
  };

  const toggleWidget = (id) => {
    setConfig(prev => ({
      ...prev,
      widgets: prev.widgets.map(w => w.id === id ? { ...w, enabled: !w.enabled } : w),
    }));
  };

  const setNavLabel = (key, val) => {
    setConfig(prev => ({ ...prev, nav_labels: { ...prev.nav_labels, [key]: val } }));
  };

  const resetNavLabel = (key) => {
    setConfig(prev => ({ ...prev, nav_labels: { ...prev.nav_labels, [key]: DEFAULT_NAV_LABELS[key] } }));
  };

  const onDragStart = useCallback((id) => setDragging(id), []);
  const onDragOver  = useCallback((id, e) => { e.preventDefault(); setDragOver(id); }, []);
  const onDrop = useCallback((targetId) => {
    if (!dragging || dragging === targetId) { setDragging(null); setDragOver(null); return; }
    setConfig(prev => {
      const ws   = [...prev.widgets];
      const from = ws.findIndex(w => w.id === dragging);
      const to   = ws.findIndex(w => w.id === targetId);
      if (from === -1 || to === -1) return prev;
      const [item] = ws.splice(from, 1);
      ws.splice(to, 0, item);
      return { ...prev, widgets: ws };
    });
    setDragging(null);
    setDragOver(null);
  }, [dragging]);

  if (!config) return <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Loading configuration…</div>;

  const enabledCount = config.widgets.filter(w => w.enabled).length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', marginBottom: 3 }}>Dashboard Configuration</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {enabledCount} widget{enabledCount !== 1 ? 's' : ''} enabled · drag to reorder
          </div>
        </div>
        <button className="btn btn-primary" style={{ minWidth: 100 }} onClick={save} disabled={saving}>
          {saving ? 'Saving…' : saved ? '✓ Saved' : 'Save changes'}
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 2, borderBottom: '1px solid var(--border)', marginBottom: 4 }}>
        {[{ id: 'widgets', label: 'Widgets' }, { id: 'nav', label: 'Navigation labels' }].map(t => (
          <button className="tab-btn" aria-selected={tab === t.id}
            key={t.id}
            onClick={() => setTab(t.id)}
           
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Widgets tab ── */}
      {tab === 'widgets' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>
            Drag items to reorder. Toggle to show or hide on the client dashboard.
            Widgets marked with a lock require a hub that is not yet enabled in Subscription settings.
          </div>

          {config.widgets.map((w) => {
            const meta   = WIDGET_CATALOGUE.find(c => c.id === w.id);
            if (!meta) return null;
            const locked = !hubActive(meta.hub);

            return (
              <div
                key={w.id}
                draggable
                onDragStart={() => onDragStart(w.id)}
                onDragOver={(e) => onDragOver(w.id, e)}
                onDrop={() => onDrop(w.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '12px 14px',
                  background: dragging === w.id
                    ? 'rgba(0,212,255,0.04)'
                    : dragOver === w.id
                    ? 'rgba(0,212,255,0.08)'
                    : locked
                    ? 'rgba(255,255,255,0.02)'
                    : 'var(--surface)',
                  border: `1px solid ${dragOver === w.id ? 'rgba(0,212,255,0.4)' : locked ? 'rgba(255,255,255,0.06)' : 'var(--border)'}`,
                  borderRadius: 8,
                  cursor: 'grab',
                  opacity: dragging === w.id ? 0.5 : locked ? 0.55 : 1,
                  transition: 'border-color 0.12s, background 0.12s',
                }}
              >
                {/* Drag handle */}
                <div style={{ color: 'var(--text-muted)', fontSize: 14, flexShrink: 0, cursor: 'grab' }}>
                  <i className="ti ti-grip-vertical" />
                </div>

                {/* Toggle — always available even when locked (pre-configure) */}
                <div
                  onClick={() => toggleWidget(w.id)}
                  title={locked ? 'Hub not enabled — widget will be hidden until hub is activated' : undefined}
                  style={{
                    width: 34, height: 20, borderRadius: 10,
                    background: w.enabled ? (locked ? 'rgba(0,212,255,0.35)' : 'var(--accent)') : 'rgba(255,255,255,0.12)',
                    position: 'relative', cursor: 'pointer', flexShrink: 0,
                    transition: 'background 0.2s',
                  }}
                >
                  <div style={{
                    position: 'absolute', top: 2,
                    left: w.enabled ? 16 : 2,
                    width: 16, height: 16, borderRadius: 8,
                    background: '#fff',
                    transition: 'left 0.2s',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                  }} />
                </div>

                {/* Lock icon */}
                {locked && (
                  <div title={`Requires ${HUB_LABELS[meta.hub]} — enable in Subscription settings`}
                    style={{ color: 'rgba(255,255,255,0.3)', fontSize: 13, flexShrink: 0 }}>
                    <i className="ti ti-lock" />
                  </div>
                )}

                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: locked ? 'var(--text-muted)' : w.enabled ? 'var(--text)' : 'var(--text-muted)' }}>
                    {meta.label}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                    {locked
                      ? <span style={{ color: 'rgba(255,200,80,0.7)' }}>Requires {HUB_LABELS[meta.hub]}</span>
                      : meta.desc}
                  </div>
                </div>

                {/* Size badge */}
                <div style={{
                  fontSize: 9, fontWeight: 700, padding: '2px 7px',
                  borderRadius: 4, background: 'var(--border)',
                  color: 'var(--text-muted)', flexShrink: 0,
                  fontFamily: 'var(--font-mono)', letterSpacing: '0.05em',
                }}>
                  {meta.size}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Nav labels tab ── */}
      {tab === 'nav' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>
            Rename navigation items for this client. Changes appear in the sidebar and page titles. Leave blank to use the default label.
          </div>

          {Object.entries(DEFAULT_NAV_LABELS).map(([key, defaultLabel]) => {
            const current = config.nav_labels[key] ?? defaultLabel;
            const changed = current !== defaultLabel;
            return (
              <div key={key} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 14px',
                background: 'var(--surface)',
                border: `1px solid ${changed ? 'rgba(0,212,255,0.3)' : 'var(--border)'}`,
                borderRadius: 8,
              }}>
                <div style={{ width: 130, flexShrink: 0 }}>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>{defaultLabel}</div>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)', opacity: 0.5, marginTop: 1 }}>Default</div>
                </div>

                <div style={{ flex: 1, position: 'relative' }}>
                  <input className="input-sm"
                    type="text"
                    value={current}
                    onChange={e => setNavLabel(key, e.target.value)}
                    placeholder={defaultLabel}
                    style={{ width: '100%' }}
                    onFocus={e => e.target.style.borderColor = 'var(--accent)'}
                    onBlur={e => e.target.style.borderColor = 'var(--border)'}
                  />
                </div>

                {changed && (
                  <button className="btn btn-quiet btn-sm" style={{ flexShrink: 0 }}
                    onClick={() => resetNavLabel(key)}
                    title="Reset to default"
                   
                  >
                    <i className="ti ti-rotate" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Save footer */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 8, borderTop: '1px solid var(--border)' }}>
        <button className="btn btn-primary" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : saved ? '✓ Saved' : 'Save changes'}
        </button>
      </div>
    </div>
  );
}
